import React from 'react';
import Modal from "react-modal";
import modalStyle from "app/data/common/modalStyle"
import { ReactComponent as CrossGrayIcon } from "images/cross_gray.svg";
import Button, { ButtonThemes } from 'ui-components/button/button';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import { Form, Formik } from 'formik';
import { useMutation } from '@tanstack/react-query';
import TerminalService from 'app/data/terminal-details/terminalService';
import { TerminalState } from 'slices/terminal-summary/TerminalDetailsState';
import { useDispatch, useSelector } from 'react-redux';
import { getTerminalSummaryByTerminalNumber, terminalDetailsSelector } from 'slices/terminal-summary/terminalDetailsSlice';
import { UpdateTerminalName } from 'app/data/terminal-details/models';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from 'app/data/common/errorMessages';

const EditTerminalName = ({
  showModal,
  handleClose,
  terminalName
}: {
  showModal: boolean, handleClose: () =>
    void, terminalName: string | undefined
}): React.ReactElement => {
  const dispatch = useDispatch();
  
  const terminalService = TerminalService.getInstance();
  const terminalState: TerminalState = useSelector(terminalDetailsSelector);

  const updateTerminalNameMutation = useMutation({
    mutationFn: ({ terminalNumber, terminalName }: UpdateTerminalName) => {
      return terminalService.updateTerminalName(terminalNumber, terminalName)
    },
    onSuccess: () => {
      toast.info("Terminal name updated successfully");
      if (terminalState.data?.terminalNumber) {
        dispatch(getTerminalSummaryByTerminalNumber(terminalState.data?.terminalNumber.toString()));
      }
      handleClose();
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });

  return (
    <Modal
      isOpen={showModal}
      style={{
        ...modalStyle, content: {
          ...modalStyle.content,
          width: "400px",
          maxWidth: "90vw",
          minWidth: "300px",
        }
      }}
      onRequestClose={() => { }}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <div className="update-terminal">
        <div className="update-terminal__close">
          <CrossGrayIcon onClick={() => handleClose()} />
        </div>
        <div className='update-terminal__title'>Edit Terminal Name</div>
        <Formik
          initialValues={{ terminalName: terminalName }}
          onSubmit={(values) => {
            updateTerminalNameMutation.mutate({
              terminalNumber: terminalState.data?.terminalNumber,
              terminalName: values.terminalName
            } as UpdateTerminalName);
          }}
        >
          {({ dirty }) =>
            <Form>
              <XGSFormInput
                type="text"
                name="terminalName"
                required={true}
                labelMode={LabelModes.column}
                className='update-terminal__form__input'
              />
              <div className="update-terminal__form__content__edit__button">
                <Button
                  theme={ButtonThemes.gray}
                  type="button"
                  className="update-terminal__form__content__edit__button__cancel"
                  spinner={updateTerminalNameMutation.isLoading}
                  onClick={() => {
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  theme={ButtonThemes.blue}
                  type="submit"
                  disabled={!dirty}
                  spinner={updateTerminalNameMutation.isLoading}
                  className="update-terminal__form__content__edit__button__submit"
                >
                  Save
                </Button>
              </div>
            </Form>}
        </Formik>
      </div>
    </Modal>
  );
};

export default EditTerminalName;
